import { API_URL_V1 } from '@/constants/ApiSetting';
import {
  BedType,
  BuildingData,
  CheckoutResponse,
  CreateDepartmentGroupPayload,
  CreateDepartmentGroupResponse,
  CreateRoomApiResponse,
  DefaultError,
  EncoderResponse,
  ExposureData,
  Facility,
  InventoryLogPayloadDateRange,
  InventoryLogPayloadSingleDate,
  InventoryLogResponse,
  IssueCardBody,
  IssueNewCardResponse,
  KeycardIssueResponse,
  MealTypeForm,
  OcrServiceResult,
  RatePlanListResponse,
  RatePlanOptions,
  RatePlanRequestPayload,
  ReorderRatePlanPayload,
  ReorderRatePlanResponse,
  ReadKeycardApiResponse,
  RoomInfo,
  RoomInfoPayload,
  RoomLocation,
  RoomTypeData,
  SearchGuestApiResponse,
  SubscriptionApiResponse,
  UpdateDepartmentGroupPayload,
  UpdateDepartmentGroupResponse,
  CreateMediaGroupPayload,
  CreateMediaGroupResponse,
  UpdateMediaGroupPayload,
  UpdateMediaGroupResponse,
  DeleteDepartmentGroupPayload,
  DeleteDepartmentGroupResponse,
  ReorderDepartmentGroupPayload,
  ReorderDepartmentGroupResponse,
  DeleteMediaGroupPayload,
  DeleteMediaGroupResponse,
  ReorderMediaGroupPayload,
  ReorderMediaGroupResponse,
  ReorderRoomTypePayload,
  ReorderRoomTypeResponse,
  DepartmentGroupListResponse,
  MediaGroupListResponse,
  PropertyInfoResponse,
  PropertyInfo,
  GenerateFileUrlResponse,
} from '@/types/types';
import axios, { AxiosError } from 'axios';
import { baseUrl, localServiceUrl, ocrServiceUrl } from './baseUrl';

export interface DuplicateBookingPayload {
  booking_id: number;
  arrival: string;
  departure: string;
  /** Array of guest objects. Index 0 is the main guest. */
  guest?: Array<Record<string, unknown> | object> | Record<string, unknown> | object;
}

export const duplicateBooking = async (payload: DuplicateBookingPayload) => {
  try {
    const response = await baseUrl.post('/pms/rpc/reservation/booking/duplicate', payload);
    const body = response?.data;
    const resultCode = body?.result_code ?? body?.data?.result_code;
    if (resultCode != null && String(resultCode) !== '0' && String(resultCode) !== '1') {
      throw body || { result_msg: 'Request failed' };
    }
    return body;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }
    throw error;
  }
};

export const updateGuest = async (payload: any) => {
  try {
    const response = await baseUrl.post('/pms/rpc/guest/update', payload);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }
    throw error;
  }
};

export const getGuestHistory = async (payload: { guest_id: number }) => {
  try {
    const response = await baseUrl.post('/pms/rpc/guest/history', payload);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }
    throw error;
  }
};

export const getBookingDetails = async (bookingId: number) => {
  try {
    const response = await baseUrl.post(`/pms/rpc/reservation/booking/get`, {
      booking_id: bookingId,
      include_logs: true,
      include_requests: true,
      include_summary: true,
      include_deposit: true,
      source: 'any',
    });
    const resultData = response.data?.data?.result_data;
    return { data: resultData };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }
    throw error;
  }
};

const getIssueCardDetails = async (
  paramName: string,
  value: string | number
): Promise<KeycardIssueResponse[]> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set(paramName, value.toString());

    const response = await baseUrl.get(`/pms/addon/keycard/issueInfo?${searchParams.toString()}`);

    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const issueNewCard = async (data: IssueCardBody): Promise<IssueNewCardResponse> => {
  try {
    const response = await localServiceUrl.post('/api/KeyCard/newKeyCard', data);

    console.log('response', response.data);

    return response?.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const issueCopyCard = async (data: IssueCardBody) => {
  try {
    const response = await localServiceUrl.post('/api/KeyCard/copyKeyCard', data);

    return response?.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

type ReadCardServiceError = {
  type: 'NETWORK' | 'TIMEOUT' | 'HTTP' | 'UNKNOWN';
  message: string;
  status?: number;
};

const readKeyCard = async (): Promise<ReadKeycardApiResponse> => {
  try {
    const response = await localServiceUrl.get('/api/KeyCard/readKeyCard', {
      timeout: 12000, // กันแขวนอีกชั้น (แม้ instance จะมีแล้วก็ตาม)
    });
    return response.data;
  } catch (error) {
    // ✅ Axios error
    if (axios.isAxiosError(error)) {
      // timeout
      if (error.code === 'ECONNABORTED') {
        const e: ReadCardServiceError = {
          type: 'TIMEOUT',
          message: 'Cannot connect to read card service (timeout)',
        };
        throw e;
      }

      // network error (ของคุณคือ ERR_NETWORK + message Network Error)
      if (error.code === 'ERR_NETWORK' || !error.response) {
        const e: ReadCardServiceError = {
          type: 'NETWORK',
          message: 'Cannot connect to read card service',
        };
        throw e;
      }

      // http error (มี response แต่ status ไม่ 2xx)
      const e: ReadCardServiceError = {
        type: 'HTTP',
        status: error.response?.status,
        message: 'Cannot connect to read card service',
      };
      throw e;
    }

    // ✅ non-axios
    const e: ReadCardServiceError = {
      type: 'UNKNOWN',
      message: 'Cannot connect to read card service',
    };
    throw e;
  }
};

const checkKeyCardService = async (): Promise<EncoderResponse> => {
  try {
    const response = await localServiceUrl.get('/api/Encoder/scan?forceScan=false');
    const { data } = response;
    console.log('data', data);
    return data;
  } catch (error) {
    throw error;
  }
};

const getRoomTypes = async (): Promise<RoomTypeData[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/roomType');
    // console.log('getRoomTypes', response?.data?.data);
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const getBedType = async (): Promise<BedType[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/bedType');
    // console.log('getRoomTypes', response?.data?.data);
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const getBuildings = async (): Promise<BuildingData[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/building');
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const getRooomLocations = async (): Promise<RoomLocation[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/roomLocation');
    // console.log('getRooomLocations', response?.data?.data);
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const getExposures = async (): Promise<ExposureData[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/roomExposure');
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const getRoomInfoList = async (): Promise<RoomInfo[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/roomInfo');
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const getRoomFacilities = async (): Promise<Facility[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/roomFacility');
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

// ✅ แปลง RoomInfo (response จริง) -> RoomInfoPayload (form)
const toRoomInfoPayload = (r: RoomInfo): RoomInfoPayload => ({
  id: r.id,
  room_number: r.room_number ?? '',
  roomtype_id: r.roomtype_id ?? null,
  building_id: r.building_id ?? null,
  bedtype_id: r.bedtype_id ?? null,
  location_id: r.location_id ?? null,
  exposure_id: r.exposure_id ?? null,

  // response ใช้ connecting_room_id
  connecting_room: r.connecting_room_id ?? [],

  // response facilities เป็น object[] -> payload ต้องเป็น number[]
  facilities: (r.facilities ?? []).map((x) => x.id),
});

const getRoomInfoById = async (id: number): Promise<RoomInfoPayload> => {
  const response = await baseUrl.get(`/pms/masterfile/roomInfo/${id}`);

  // backend ส่ง array -> เอาตัวแรก
  const item: RoomInfo | undefined = response?.data?.data?.[0];

  if (!item) {
    throw new Error(`RoomInfo not found id=${id}`);
  }

  const payload = toRoomInfoPayload(item);

  return payload;
};

const updateRoomInfo = async (payload: RoomInfoPayload) => {
  try {
    const response = await baseUrl.post('pms/masterfile/roomInfo/update', payload);

    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const createRoomInfo = async (
  payload: RoomInfoPayload
): Promise<CreateRoomApiResponse | DefaultError> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/roominfo/create', payload);
    // console.log(response?.data?.data);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const checkout = async (
  register_id: number,
  early_checkout_reason?: string
): Promise<CheckoutResponse> => {
  try {
    const payload = {
      register_id,
      early_checkout_reason: early_checkout_reason || null,
    };
    const response = await baseUrl.post('/pms/registration/checkOut', payload);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const getAvailableRate = async (payload: RatePlanRequestPayload): Promise<RatePlanListResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/inventory/get_available_rates', payload);
    const body = response?.data;
    const resultCode = body?.result_code ?? body?.data?.result_code;
    if (resultCode != null && String(resultCode) !== '0' && String(resultCode) !== '1') {
      throw body?.data ?? body ?? { result_msg: 'Request failed' };
    }
    return body?.data ?? body?.result_data ?? body;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const createNewMealType = async (payload: MealTypeForm): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/mealtype/create', payload);
    console.log(response?.data?.data);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const updateMealType = async (payload: MealTypeForm): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/mealtype/update', payload);
    console.log(response?.data?.data);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const acceptPrivacyAndTermCondition = async (accepted: boolean): Promise<any> => {
  try {
    const response = await baseUrl.post('/auth/rpc/user/update_policy_status', {
      policy_accepted: accepted,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

const ocrServiceProcess = async (
  imageFile: File,
  docTypeHint?: 'PASSPORT' | 'THAI_ID_CARD'
): Promise<OcrServiceResult> => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    if (docTypeHint) formData.append('docTypeHint', docTypeHint);

    const urlPath = docTypeHint === 'THAI_ID_CARD' ? '/v1/ocr' : '/v1/passport_mrz';

    const res = await ocrServiceUrl.post(urlPath, formData);

    return res.data; // API returns the object directly, not wrapped in .data.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || 'Something went wrong');
    }
    throw new Error('Something went wrong');
  }
};

const searchExistingGuest = async (payload: {
  first_name: string;
  last_name: string;
}): Promise<SearchGuestApiResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/guest/find_similar_guest', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    } else {
      throw error;
    }
  }
};

const getSubscriptionInfo = async (): Promise<SubscriptionApiResponse> => {
  try {
    const response = await baseUrl.post('/auth/rpc/subscription/get_info', {});
    console.log('response', response.data);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

const reorderRatePlan = async (
  payload: ReorderRatePlanPayload
): Promise<ReorderRatePlanResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/rateplan/reorder', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const getRatePlansList = async (): Promise<RatePlanOptions[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/ratePlan');
    console.log('response - getRatePlansList', response.data?.data);
    return response?.data?.data;
  } catch (error) {
    return [];
  }
};

const getRateCalendarLog = async (
  payload: InventoryLogPayloadSingleDate
): Promise<InventoryLogResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/inventory/ratecalendar_get_logs', payload);

    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const createNewDepartmentGroup = async (
  payload: CreateDepartmentGroupPayload
): Promise<CreateDepartmentGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/departmentgroup/create', payload);
    console.log('response - createNewDepartmentGroup', response.data?.data);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const updateDepartmentGroup = async (
  payload: UpdateDepartmentGroupPayload
): Promise<UpdateDepartmentGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/departmentgroup/update', payload);
    console.log('response - updateDepartmentGroup', response.data?.data);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const getDepartmentGroupList = async (): Promise<DepartmentGroupListResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/departmentgroup/list', {});
    const body = response?.data?.data;
    if (body?.result_code != null && String(body.result_code) !== '0') throw body;
    return body;
  } catch (error) {
    throw error;
  }
};

const getMediaGroupList = async (): Promise<MediaGroupListResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/mediagroup/list', {});
    const body = response?.data?.data;
    if (body?.result_code != null && String(body.result_code) !== '0') throw body;
    return body;
  } catch (error) {
    throw error;
  }
};

const deleteDepartmentGroup = async (
  payload: DeleteDepartmentGroupPayload
): Promise<DeleteDepartmentGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/departmentgroup/delete', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const reorderDepartmentGroup = async (
  payload: ReorderDepartmentGroupPayload
): Promise<ReorderDepartmentGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/departmentgroup/reorder', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const reorderMediaGroup = async (
  payload: ReorderMediaGroupPayload
): Promise<ReorderMediaGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/mediagroup/reorder', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const reorderRoomType = async (
  payload: ReorderRoomTypePayload
): Promise<ReorderRoomTypeResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/roomtype/reorder', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteMediaGroup = async (
  payload: DeleteMediaGroupPayload
): Promise<DeleteMediaGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/mediagroup/delete', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const createNewMediaGroup = async (
  payload: CreateMediaGroupPayload
): Promise<CreateMediaGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/mediagroup/create', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const updateMediaGroup = async (
  payload: UpdateMediaGroupPayload
): Promise<UpdateMediaGroupResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/mediagroup/update', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const getPropertyInfo = async (): Promise<PropertyInfoResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/propertyinfo/get', {});
    console.log('response - getPropertyInfo', response.data?.data);
    const body = response?.data?.data;
    return body;
  } catch (error) {
    throw error;
  }
};

const updatePropertyInfo = async (payload: PropertyInfo): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/rpc/masterfile/propertyinfo/update', payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};

const generatePropertyLogoUrl = async (fileName: string): Promise<GenerateFileUrlResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/utility/gen_file_url', {
      file_category: 'hotel_logo',
      file_name: fileName, // 'example.png'
    });
    console.log('response - getPropertyInfo', response.data?.data);
    const body = response?.data?.data;
    return body;
  } catch (error) {
    throw error;
  }
};

// === BedType CRUD ===
const createBedType = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/bedType/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateBedType = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/bedType/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteBedType = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/bedType/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

// === RoomGroup CRUD ===
const getRoomGroups = async (): Promise<any[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/roomgroup');
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const generateS3FileUrl = async (payload: {
  file_name: string;
  file_category:
    | 'guest_photo'
    | 'guest_document'
    | 'guest_sign'
    | 'booking_file'
    | 'registration_file'
    | 'hotel_logo'
    | 'bed_image';
}): Promise<GenerateFileUrlResponse> => {
  try {
    const response = await baseUrl.post('/pms/rpc/utility/gen_file_url', payload);
    console.log('response - getPropertyInfo', response.data?.data);
    const body = response?.data?.data;
    return body;
  } catch (error) {
    throw error;
  }
};

const createRoomGroup = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomgroup/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateRoomGroup = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomgroup/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteRoomGroup = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomgroup/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

// === Location CRUD ===
const createRoomLocation = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomLocation/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateRoomLocation = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomLocation/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteRoomLocation = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomLocation/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

// === Building CRUD ===
const createBuilding = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/building/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateBuilding = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/building/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteBuilding = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/building/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

// === Exposure CRUD ===
const createExposure = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomExposure/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateExposure = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomExposure/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteExposure = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomExposure/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

// === Facility CRUD ===
const createFacility = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomFacility/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateFacility = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomFacility/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteFacility = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/roomFacility/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

// === OutOfOrder Reason CRUD ===
const getOutOfOrderReasons = async (): Promise<any[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/ooo');
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const createOutOfOrderReason = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/ooo/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateOutOfOrderReason = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/ooo/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteOutOfOrderReason = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/ooo/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

// === RateGroup CRUD ===
const getRateGroups = async (): Promise<any[]> => {
  try {
    const response = await baseUrl.get('/pms/masterfile/rategroup');
    return response?.data?.data || [];
  } catch (error) {
    throw error;
  }
};

const createRateGroup = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/rategroup/create/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const updateRateGroup = async (payload: any): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/rategroup/update/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

const deleteRateGroup = async (payload: { id: number }): Promise<any> => {
  try {
    const response = await baseUrl.post('/pms/masterfile/rategroup/delete/', payload);
    return response?.data?.data;
  } catch (error) {
    if (error instanceof AxiosError) throw error.response?.data;
    throw error;
  }
};

export {
  getIssueCardDetails,
  issueNewCard,
  issueCopyCard,
  checkKeyCardService,
  getRoomTypes,
  getBedType,
  createBedType,
  updateBedType,
  deleteBedType,
  getBuildings,
  createBuilding,
  updateBuilding,
  deleteBuilding,
  getRooomLocations,
  createRoomLocation,
  updateRoomLocation,
  deleteRoomLocation,
  getExposures,
  createExposure,
  updateExposure,
  deleteExposure,
  getRoomFacilities,
  createFacility,
  updateFacility,
  deleteFacility,
  getRoomGroups,
  createRoomGroup,
  updateRoomGroup,
  deleteRoomGroup,
  getOutOfOrderReasons,
  createOutOfOrderReason,
  updateOutOfOrderReason,
  deleteOutOfOrderReason,
  getRateGroups,
  createRateGroup,
  updateRateGroup,
  deleteRateGroup,
  getRoomInfoList,
  getRoomInfoById,
  updateRoomInfo,
  createRoomInfo,
  readKeyCard,
  checkout,
  getUnassignedCounts,
  getUnassignedDetails,
  autoBlockByBookingItem,
  autoBlockByDate,
  getAvailableRate,
  createNewMealType,
  updateMealType,
  acceptPrivacyAndTermCondition,
  ocrServiceProcess,
  searchExistingGuest,
  getSubscriptionInfo,
  getRatePlansList,
  getRateCalendarLog,
  getDepartmentGroupList,
  createNewDepartmentGroup,
  updateDepartmentGroup,
  deleteDepartmentGroup,
  reorderDepartmentGroup,
  reorderMediaGroup,
  reorderRatePlan,
  reorderRoomType,
  getMediaGroupList,
  createNewMediaGroup,
  updateMediaGroup,
  deleteMediaGroup,
  getPropertyInfo,
  updatePropertyInfo,
  generatePropertyLogoUrl,
  generateS3FileUrl,
};

// --- Unassigned Rooms API ---

export interface UnassignedCountsPayload {
  start_date: string;
  end_date: string;
}

export interface UnassignedCountsResponse {
  result_data: number[]; // Array of counts, mapped by index from start_date
}

const getUnassignedCounts = async (
  payload: UnassignedCountsPayload,
  signal?: AbortSignal
): Promise<number[]> => {
  try {
    const response = await baseUrl.post('/pms/rpc/inventory/get_unassigned_rooms', payload, {
      signal,
    });
    return response?.data?.data?.result_data || response?.data?.result_data || [];
  } catch (error) {
    throw error;
  }
};

export interface UnassignedDetail {
  booking_id: number;
  booking_item: number;
  status: string;
  roomtype_code: string;
  guest_name: string;
  channel_name: string | null;
  arrival: string;
  departure: string;
  assigned: number;
  unassigned: number;
  total: number;
}

const getUnassignedDetails = async (
  date: string,
  signal?: AbortSignal
): Promise<UnassignedDetail[]> => {
  try {
    const response = await baseUrl.post(
      '/pms/rpc/inventory/get_unassigned_details',
      { date },
      { signal }
    );
    const data = response?.data?.data;
    // Check if data is array
    return Array.isArray(data) ? data : data?.result_data || [];
  } catch (error) {
    throw error;
  }
};

export interface AutoBlockPayload {
  booking_id?: number;
  booking_item?: number;
  date?: string; // For autoBlockByDate
  bedtypes?: number[];
  locations?: number[];
  buildings?: number[];
  exposures?: number[];
  super_block?: boolean;
}

// Auto-block on a large property can legitimately run well past the default 30s
// instance timeout, so give these calls a generous per-request timeout. Without
// this the request is aborted mid-flight and the UI shows a misleading "no rooms
// blocked" message instead of a clear timeout.
const AUTO_BLOCK_TIMEOUT = 120_000;

// Preserve the backend response body when present, but always attach transport
// info so the caller can tell a timeout / network drop apart from a real
// "no rooms blocked" business response (on timeout `error.response` is undefined,
// so the old `throw error.response?.data` discarded everything).
const normalizeAutoBlockError = (error: unknown) => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as any;
    return {
      ...(data && typeof data === 'object' ? data : {}),
      result_msg: data?.result_msg ?? data?.message,
      message: data?.message ?? error.message,
      __isTimeout: error.code === 'ECONNABORTED',
      __isNetworkError: error.code === 'ERR_NETWORK' || !error.response,
      __httpStatus: error.response?.status,
      __code: error.code,
    };
  }
  return error;
};

const autoBlockByBookingItem = async (payload: AutoBlockPayload) => {
  try {
    const response = await baseUrl.post('/pms/reservation/blockRoom/AutoBlock', payload, {
      timeout: AUTO_BLOCK_TIMEOUT,
    });
    return response?.data;
  } catch (error) {
    throw normalizeAutoBlockError(error);
  }
};

const autoBlockByDate = async (payload: AutoBlockPayload) => {
  try {
    const response = await baseUrl.post(
      '/pms/rpc/reservation/blockroom/autoblock_by_date',
      payload,
      { timeout: AUTO_BLOCK_TIMEOUT }
    );
    return response?.data;
  } catch (error) {
    throw normalizeAutoBlockError(error);
  }
};
